import React, { useState, useEffect, useMemo } from 'react';
import { db, getCollectionName, auth } from './firebase';
import {
  collection, query, onSnapshot, orderBy, doc,
  addDoc, updateDoc, deleteDoc, serverTimestamp, where,
} from './firestore-compat';
import { supabase } from './supabase';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// ── Constantes ────────────────────────────────────────────────────────────────
const UNIDADES = ['Homebaby', 'Vitalcare'];
const ITEM_CATEGORIAS = [
  'PROFISSIONAIS', 'MATERIAIS', 'MEDICAMENTOS', 'EQUIPAMENTOS', 'OXIGÊNIO',
  'DIETA', 'EXAMES LABORATORIAIS', 'REMOÇÃO', 'OPME', 'ESPECIALISTA', 'EXAME DE IMAGEM',
  'VACINA', 'TERAPIAS ESPECIAIS',
];
const STATUS_OPTS = ['ativo', 'inativo', 'arquivado'];
const STATUS_LABEL = { ativo: 'Ativo', inativo: 'Inativo', arquivado: 'Arquivado' };
const STATUS_COLOR = {
  ativo:     { bg: '#dcfce7', text: '#15803d' },
  inativo:   { bg: '#fef9c3', text: '#a16207' },
  arquivado: { bg: '#f1f5f9', text: '#64748b' },
};
const PARAM_LABELS = { materiais: 'Materiais Descartáveis', medicamentos: 'Medicamentos', dietas: 'Dietas' };

const P = {
  bg: '#f8fafc', white: '#fff', border: '#e2e8f0', text: '#1e293b',
  muted: '#64748b', primary: '#6366f1', green: '#22c55e',
  red: '#ef4444', amber: '#f59e0b', blue: '#3b82f6',
};

const emptyItens = () => ITEM_CATEGORIAS.map(categoria => ({ categoria, incluso: '', excluso: '' }));
const emptyParametros = () => ({
  materiais:    { tabela: '', percentual: '', observacoes: '' },
  medicamentos: { tabela: '', percentual: '', observacoes: '' },
  dietas:       { tabela: '', percentual: '', observacoes: '' },
});
const EMPTY_CONTRATO = { unidade: 'Homebaby', nomeContrato: '', dataVigencia: '', status: 'ativo', observacoes: '' };
const EMPTY_PACOTE = { nome: '', ordem: 1, itens: emptyItens() };

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d)) return '—';
    return d.toLocaleString('pt-BR');
  } catch { return '—'; }
}
function itensPreenchidos(itens) {
  return (itens || []).filter(i => (i.incluso || '').trim() || (i.excluso || '').trim()).length;
}
function safeFileName(s) {
  return (s || 'contrato').replace(/[\\/:*?"<>|[\]]/g, '_').slice(0, 60);
}

// ── Componentes base ──────────────────────────────────────────────────────────
function Pill({ label, color, bg, small }) {
  return (
    <span style={{
      display: 'inline-block', padding: small ? '2px 8px' : '3px 10px',
      borderRadius: 20, fontSize: small ? '0.68rem' : '0.72rem',
      fontWeight: 700, color, background: bg, whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}
function StatusBadge({ status, small }) {
  const c = STATUS_COLOR[status] || STATUS_COLOR.ativo;
  return <Pill label={STATUS_LABEL[status] || status} color={c.text} bg={c.bg} small={small} />;
}
function UnidadeBadge({ unidade, small }) {
  const c = unidade === 'Homebaby' ? { bg: '#ede9fe', text: '#6d28d9' } : { bg: '#d1fae5', text: '#065f46' };
  return <Pill label={unidade} color={c.text} bg={c.bg} small={small} />;
}

const inp = {
  width: '100%', padding: '7px 10px', border: `1px solid ${P.border}`,
  borderRadius: 6, fontSize: '0.85rem', color: P.text, background: P.white, boxSizing: 'border-box',
};

function Field({ label, children, required }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: P.muted, marginBottom: 4 }}>
        {label}{required && <span style={{ color: P.red }}> *</span>}
      </label>
      {children}
    </div>
  );
}
function Inp({ value, onChange, type = 'text', placeholder, min, max }) {
  return <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} min={min} max={max} style={inp} />;
}
function Sel({ value, onChange, options, placeholder, labels }) {
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)} style={inp}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o} value={o}>{labels ? labels[o] : o}</option>)}
    </select>
  );
}
function Txt({ value, onChange, rows = 2, placeholder }) {
  return <textarea value={value ?? ''} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder}
    style={{ ...inp, resize: 'vertical', lineHeight: 1.5, fontFamily: 'inherit' }} />;
}
function Btn({ children, onClick, color = P.primary, disabled, small, outline }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: small ? '5px 12px' : '8px 18px',
      background: outline ? 'transparent' : color,
      color: outline ? color : '#fff',
      border: `1.5px solid ${color}`,
      borderRadius: 7, fontSize: small ? '0.78rem' : '0.85rem',
      fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap',
    }}>{children}</button>
  );
}
function GhostBtn({ children, onClick, small, title }) {
  return (
    <button onClick={onClick} title={title} style={{
      background: 'none', border: `1px solid ${P.border}`, borderRadius: 6,
      padding: small ? '3px 7px' : '5px 10px', fontSize: small ? '0.72rem' : '0.8rem',
      cursor: 'pointer', color: P.muted,
    }}>{children}</button>
  );
}
function Modal({ title, onClose, children, width = 560 }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: P.white, borderRadius: 12, width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px 14px', borderBottom: `1px solid ${P.border}`, position: 'sticky', top: 0, background: P.white, zIndex: 1 }}>
          <span style={{ fontWeight: 700, fontSize: '1rem', color: P.text }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: P.muted }}>✕</button>
        </div>
        <div style={{ padding: '18px 22px 22px' }}>{children}</div>
      </div>
    </div>
  );
}

// ── Cópia guardada (versão anterior) ──────────────────────────────────────────
// O banco guarda sozinho como o contrato/pacote estava antes de cada alteração ou exclusão
// (tabela regras_contrato_versoes, gatilho no banco — vale para a tela, importações e scripts).
function nomeDaVersao(v) {
  return v.tipo === 'pacote' ? `Pacote "${v.dados?.nome || '—'}"` : 'Dados do contrato e parâmetros';
}
function VersaoDetalhe({ versao }) {
  const d = versao.dados || {};
  if (versao.tipo === 'pacote') {
    const itens = (d.itens || []).filter(i => (i.incluso || '').trim() || (i.excluso || '').trim());
    return (
      <div>
        <div style={{ fontSize: '0.8rem', color: P.muted, marginBottom: 8 }}>Pacote <b style={{ color: P.text }}>{d.nome}</b> · ordem {d.ordem || '—'}</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
          <thead><tr style={{ background: P.bg }}>
            {['Item', 'Incluso no pacote', 'Excluso do pacote'].map(h => <th key={h} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${P.border}` }}>{h}</th>)}
          </tr></thead>
          <tbody>{itens.map(i => (
            <tr key={i.categoria} style={{ borderBottom: `1px solid ${P.border}`, verticalAlign: 'top' }}>
              <td style={{ padding: '6px 8px', fontWeight: 600 }}>{i.categoria}</td>
              <td style={{ padding: '6px 8px' }}>{i.incluso || '—'}</td>
              <td style={{ padding: '6px 8px' }}>{i.excluso || '—'}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    );
  }
  const par = d.parametros || {};
  return (
    <div style={{ fontSize: '0.82rem', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div><b>Contrato:</b> {d.nomeContrato} · <b>Unidade:</b> {d.unidade} · <b>Status:</b> {STATUS_LABEL[d.status] || d.status || '—'} · <b>Vigência:</b> {fmtDate(d.dataVigencia)}</div>
      {['materiais', 'medicamentos', 'dietas'].map(k => (
        <div key={k}><b style={{ textTransform: 'capitalize' }}>{k}:</b> {[par[k]?.tabela, par[k]?.percentual !== '' && par[k]?.percentual != null ? `${par[k].percentual}%` : '', par[k]?.observacoes].filter(Boolean).join(' · ') || '—'}</div>
      ))}
      <div><b>Observações:</b><div style={{ whiteSpace: 'pre-wrap', color: P.text, marginTop: 2 }}>{d.observacoes || '—'}</div></div>
    </div>
  );
}

// ── Exportação ────────────────────────────────────────────────────────────────
function contratoParaLinhas(pacotesDoContrato) {
  const linhas = [];
  [...pacotesDoContrato].sort((a, b) => (a.ordem || 0) - (b.ordem || 0)).forEach(p => {
    (p.itens || []).forEach(it => {
      linhas.push({
        Pacote: p.nome, Categoria: it.categoria,
        'Incluso no Pacote': it.incluso || '', 'Excluso do Pacote': it.excluso || '',
      });
    });
  });
  return linhas;
}

function parametrosParaLinhas(contrato) {
  const params = contrato.parametros || emptyParametros();
  return ['materiais', 'medicamentos', 'dietas'].map(k => ({
    Parâmetro: PARAM_LABELS[k],
    Tabela: params[k]?.tabela || '',
    'Percentual (%)': params[k]?.percentual ?? '',
    Observações: params[k]?.observacoes || '',
  }));
}

function exportarXLSX(contrato, pacotesDoContrato) {
  const wb = XLSX.utils.book_new();
  const linhas = contratoParaLinhas(pacotesDoContrato);
  const ws = XLSX.utils.json_to_sheet(linhas.length ? linhas : [{ Aviso: 'Nenhum pacote cadastrado' }]);
  XLSX.utils.book_append_sheet(wb, ws, 'Pacotes');
  const wsParams = XLSX.utils.json_to_sheet(parametrosParaLinhas(contrato));
  XLSX.utils.book_append_sheet(wb, wsParams, 'Parâmetros');
  XLSX.writeFile(wb, `${safeFileName(contrato.nomeContrato)}.xlsx`);
}

function exportarMultiplosXLSX(contratos, pacotesPorContrato) {
  const wb = XLSX.utils.book_new();
  contratos.forEach((c, idx) => {
    const linhas = contratoParaLinhas(pacotesPorContrato[c.id] || []);
    const ws = XLSX.utils.json_to_sheet(linhas.length ? linhas : [{ Aviso: 'Nenhum pacote cadastrado' }]);
    const base = safeFileName(c.nomeContrato || `Contrato ${idx + 1}`).slice(0, 28) || `Contrato${idx + 1}`;
    XLSX.utils.book_append_sheet(wb, ws, base);
  });
  XLSX.writeFile(wb, `regras_contrato_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

function desenharContratoPDF(pdfDoc, contrato, pacotesDoContrato, startY) {
  let y = startY;
  pdfDoc.setFontSize(14);
  pdfDoc.text(`Regras de Contrato — ${contrato.nomeContrato || ''}`, 14, y);
  y += 7;
  pdfDoc.setFontSize(9);
  pdfDoc.text(`Unidade: ${contrato.unidade || '—'}  ·  Status: ${STATUS_LABEL[contrato.status] || '—'}  ·  Vigência: ${fmtDate(contrato.dataVigencia)}`, 14, y);
  y += 7;
  if (contrato.observacoes) {
    pdfDoc.text(`Obs: ${contrato.observacoes}`, 14, y);
    y += 7;
  }

  [...pacotesDoContrato].sort((a, b) => (a.ordem || 0) - (b.ordem || 0)).forEach(p => {
    pdfDoc.setFontSize(11);
    pdfDoc.text(`Pacote: ${p.nome}`, 14, y);
    autoTable(pdfDoc, {
      startY: y + 3,
      head: [['Categoria', 'Incluso no Pacote', 'Excluso do Pacote']],
      body: (p.itens || []).map(it => [it.categoria, it.incluso || '—', it.excluso || '—']),
      styles: { fontSize: 8, cellWidth: 'wrap' },
      columnStyles: { 0: { cellWidth: 32 } },
      margin: { left: 14, right: 14 },
    });
    y = (pdfDoc.lastAutoTable?.finalY || y + 40) + 10;
    if (y > 250) { pdfDoc.addPage(); y = 20; }
  });

  const params = contrato.parametros || emptyParametros();
  pdfDoc.setFontSize(11);
  pdfDoc.text('Parâmetros', 14, y);
  autoTable(pdfDoc, {
    startY: y + 3,
    head: [['Parâmetro', 'Tabela', '%', 'Observações']],
    body: ['materiais', 'medicamentos', 'dietas'].map(k => [
      PARAM_LABELS[k], params[k]?.tabela || '—', String(params[k]?.percentual ?? '—'), params[k]?.observacoes || '—',
    ]),
    styles: { fontSize: 8 },
    margin: { left: 14, right: 14 },
  });
  return pdfDoc.lastAutoTable?.finalY || y + 30;
}

function exportarPDF(contrato, pacotesDoContrato) {
  const pdfDoc = new jsPDF();
  desenharContratoPDF(pdfDoc, contrato, pacotesDoContrato, 16);
  pdfDoc.save(`${safeFileName(contrato.nomeContrato)}.pdf`);
}

function exportarMultiplosPDF(contratos, pacotesPorContrato) {
  const pdfDoc = new jsPDF();
  contratos.forEach((contrato, idx) => {
    if (idx > 0) pdfDoc.addPage();
    desenharContratoPDF(pdfDoc, contrato, pacotesPorContrato[contrato.id] || [], 16);
  });
  pdfDoc.save(`regras_contrato_lote_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function RegrasContrato() {
  const [contratos, setContratos] = useState([]);
  const [pacotes, setPacotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userPerms, setUserPerms] = useState({});
  const [userRoles, setUserRoles] = useState([]);
  const [userName, setUserName] = useState('');

  const currentUser = auth.currentUser;
  const isSuperAdmin = currentUser?.email === 'alexandrebareaoliveira@gmail.com';
  const canEdit = isSuperAdmin || userRoles.includes('admin') || userRoles.includes('administrador') || userPerms?.regras_contrato?.edit === true;
  const usuarioAtual = userName || currentUser?.email || 'desconhecido';

  const [unidadeSel, setUnidadeSel] = useState(() => sessionStorage.getItem('rc_unidade') || 'Todas');
  useEffect(() => { sessionStorage.setItem('rc_unidade', unidadeSel); }, [unidadeSel]);

  const [search, setSearch] = useState('');
  const [showFiltros, setShowFiltros] = useState(false);
  const [fStatus, setFStatus] = useState('');
  const [fDIni, setFDIni] = useState('');
  const [fDFim, setFDFim] = useState('');
  const [fOperadora, setFOperadora] = useState('');

  const [expandedC, setExpandedC] = useState({});
  const [expandedP, setExpandedP] = useState({});

  const [modalContrato, setModalContrato] = useState(false);
  const [editContrato, setEditContrato] = useState(null);
  const [formContrato, setFormContrato] = useState(EMPTY_CONTRATO);
  const [errContrato, setErrContrato] = useState('');

  const [modalPacote, setModalPacote] = useState(false);
  const [pacoteContratoId, setPacoteContratoId] = useState(null);
  const [editPacote, setEditPacote] = useState(null);
  const [formPacote, setFormPacote] = useState(EMPTY_PACOTE);

  const [modalParametros, setModalParametros] = useState(null); // contrato selecionado
  const [formParametros, setFormParametros] = useState(emptyParametros());

  const [delItem, setDelItem] = useState(null); // { tipo:'contrato'|'pacote', id, nome }
  const [historicoContrato, setHistoricoContrato] = useState(null);
  const [versoes, setVersoes] = useState([]);
  const [verVersao, setVerVersao] = useState(null);

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showCompare, setShowCompare] = useState(false);

  const [saving, setSaving] = useState(false);

  const colContrato = getCollectionName('regras_contrato');
  const colPacote = getCollectionName('regras_contrato_pacotes');

  useEffect(() => {
    let loaded = 0;
    const done = () => { loaded++; if (loaded === 2) setLoading(false); };
    const u1 = onSnapshot(query(collection(db, colContrato), orderBy('criadoEm', 'desc')), snap => {
      setContratos(snap.docs.map(d => ({ id: d.id, ...d.data() }))); done();
    });
    const u2 = onSnapshot(collection(db, colPacote), snap => {
      setPacotes(snap.docs.map(d => ({ id: d.id, ...d.data() }))); done();
    });
    return () => { u1(); u2(); };
  }, [colContrato, colPacote]);

  useEffect(() => {
    if (!currentUser?.email) return;
    const q = query(collection(db, getCollectionName('users')), where('email', '==', currentUser.email.toLowerCase()));
    return onSnapshot(q, snap => {
      if (!snap.empty) {
        const u = snap.docs[0].data();
        setUserRoles(Array.isArray(u.roles) ? u.roles : (u.role ? [u.role] : []));
        setUserPerms(u.permissions || {});
        setUserName(u.name || currentUser.email);
      } else {
        setUserName(currentUser.email || '');
      }
    }, () => {});
  }, [currentUser]);

  const pacotesPorContrato = useMemo(() => {
    const map = {};
    pacotes.forEach(p => {
      if (!map[p.contratoId]) map[p.contratoId] = [];
      map[p.contratoId].push(p);
    });
    Object.values(map).forEach(list => list.sort((a, b) => (a.ordem || 0) - (b.ordem || 0)));
    return map;
  }, [pacotes]);

  // ── Filtros ──────────────────────────────────────────────────────────────
  const contratosFiltrados = useMemo(() => {
    let list = contratos;
    if (unidadeSel !== 'Todas') list = list.filter(c => c.unidade === unidadeSel);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(c => {
        if ((c.nomeContrato || '').toLowerCase().includes(s)) return true;
        const pacotesDoC = pacotesPorContrato[c.id] || [];
        return pacotesDoC.some(p => (p.nome || '').toLowerCase().includes(s));
      });
    }
    if (fStatus) list = list.filter(c => c.status === fStatus);
    if (fDIni) list = list.filter(c => !c.dataVigencia || c.dataVigencia >= fDIni);
    if (fDFim) list = list.filter(c => !c.dataVigencia || c.dataVigencia <= fDFim);
    if (fOperadora.trim()) {
      const s = fOperadora.toLowerCase();
      list = list.filter(c => (c.nomeContrato || '').toLowerCase().includes(s));
    }
    return [...list].sort((a, b) => (a.nomeContrato || '').localeCompare(b.nomeContrato || ''));
  }, [contratos, unidadeSel, search, fStatus, fDIni, fDFim, fOperadora, pacotesPorContrato]);

  const contratosPorUnidade = useMemo(() => {
    if (unidadeSel !== 'Todas') return { [unidadeSel]: contratosFiltrados };
    const map = { Homebaby: [], Vitalcare: [] };
    contratosFiltrados.forEach(c => {
      if (!map[c.unidade]) map[c.unidade] = [];
      map[c.unidade].push(c);
    });
    return map;
  }, [contratosFiltrados, unidadeSel]);

  const hasF = fStatus || fDIni || fDFim || fOperadora;
  const clrF = () => { setFStatus(''); setFDIni(''); setFDFim(''); setFOperadora(''); };

  // ── Histórico helper ─────────────────────────────────────────────────────
  const addHistorico = (contrato, acao) => {
    const entry = { acao, usuario: usuarioAtual, data: new Date().toISOString() };
    return [...(contrato.historico || []), entry];
  };

  // ── Cópias guardadas (versões anteriores) ────────────────────────────────
  const carregarVersoes = async contratoId => {
    const { data, error } = await supabase.from('regras_contrato_versoes')
      .select('id, tipo, operacao, pacote_id, dados, salvo_em').eq('contrato_id', contratoId).order('salvo_em');
    setVersoes(error ? [] : (data || []));
  };
  useEffect(() => {
    setVersoes([]);
    if (historicoContrato?.id) carregarVersoes(historicoContrato.id);
  }, [historicoContrato?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const restaurarVersao = async v => {
    const contrato = contratos.find(c => c.id === historicoContrato?.id);
    if (!contrato) return;
    const quando = fmtDateTime(v.salvo_em);
    if (!window.confirm(`Voltar ${nomeDaVersao(v)} para como estava antes de ${quando}?\n\nA versão atual também fica guardada no histórico.`)) return;
    setSaving(true);
    try {
      const d = v.dados || {};
      if (v.tipo === 'pacote') {
        const campos = { nome: d.nome || '', ordem: d.ordem || 1, itens: d.itens || [] };
        if (pacotes.some(p => p.id === v.pacote_id)) {
          await updateDoc(doc(db, colPacote, v.pacote_id), { ...campos, editadoPor: usuarioAtual, editadoEm: serverTimestamp() });
        } else {
          await addDoc(collection(db, colPacote), { ...campos, contratoId: contrato.id, criadoPor: usuarioAtual, criadoEm: serverTimestamp() });
        }
      } else {
        const { historico, criadoEm, criadoPor, editadoEm, editadoPor, ...rest } = d;
        await updateDoc(doc(db, colContrato, contrato.id), { ...rest, editadoPor: usuarioAtual, editadoEm: serverTimestamp() });
      }
      const historicoNovo = addHistorico(contrato, `${nomeDaVersao(v)} restaurado para a cópia de ${quando}`);
      await updateDoc(doc(db, colContrato, contrato.id), { historico: historicoNovo });
      setHistoricoContrato({ ...contrato, historico: historicoNovo });
      setVerVersao(null);
      await carregarVersoes(contrato.id);
    } catch (e) { alert('Erro ao restaurar: ' + e.message); }
    setSaving(false);
  };

  // Linha do tempo do histórico: anotações + cópias guardadas, da mais nova para a mais antiga
  const linhaDoTempo = useMemo(() => {
    if (!historicoContrato) return [];
    const atual = contratos.find(c => c.id === historicoContrato.id) || historicoContrato;
    return [
      ...(atual.historico || []).map((h, i) => ({ k: `h${i}`, quando: h.data, h })),
      ...versoes.map(v => ({ k: `v${v.id}`, quando: v.salvo_em, v })),
    ].sort((a, b) => (new Date(b.quando || 0) - new Date(a.quando || 0)) || (a.v ? 1 : -1));
  }, [historicoContrato, contratos, versoes]);

  // ── CRUD Contrato ────────────────────────────────────────────────────────
  const openNewContrato = () => { setEditContrato(null); setFormContrato(EMPTY_CONTRATO); setErrContrato(''); setModalContrato(true); };
  const openEditContrato = c => {
    const { unidade, nomeContrato, dataVigencia, status, observacoes } = c;
    setEditContrato(c);
    setFormContrato({ unidade, nomeContrato, dataVigencia: dataVigencia || '', status: status || 'ativo', observacoes: observacoes || '' });
    setErrContrato('');
    setModalContrato(true);
  };
  const closeContrato = () => { setModalContrato(false); setEditContrato(null); setErrContrato(''); };

  const saveContrato = async () => {
    if (!formContrato.nomeContrato.trim()) { setErrContrato('Nome do contrato é obrigatório.'); return; }
    const dup = contratos.find(c =>
      c.id !== editContrato?.id &&
      c.unidade === formContrato.unidade &&
      (c.nomeContrato || '').trim().toLowerCase() === formContrato.nomeContrato.trim().toLowerCase()
    );
    if (dup) { setErrContrato(`Já existe um contrato "${formContrato.nomeContrato}" na unidade ${formContrato.unidade}.`); return; }

    setSaving(true);
    try {
      if (editContrato) {
        const historico = addHistorico(editContrato, 'Dados do contrato editados');
        await updateDoc(doc(db, colContrato, editContrato.id), {
          ...formContrato, historico, editadoPor: usuarioAtual, editadoEm: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, colContrato), {
          ...formContrato, parametros: emptyParametros(),
          historico: [{ acao: 'Contrato criado', usuario: usuarioAtual, data: new Date().toISOString() }],
          criadoPor: usuarioAtual, criadoEm: serverTimestamp(),
        });
      }
      closeContrato();
    } catch (e) { alert('Erro ao salvar contrato: ' + e.message); }
    setSaving(false);
  };

  const duplicarContrato = async c => {
    setSaving(true);
    try {
      const { id, historico, criadoEm, editadoEm, criadoPor, editadoPor, ...rest } = c;
      const novoDoc = await addDoc(collection(db, colContrato), {
        ...rest, nomeContrato: `${c.nomeContrato} (cópia)`,
        historico: [{ acao: `Duplicado de "${c.nomeContrato}"`, usuario: usuarioAtual, data: new Date().toISOString() }],
        criadoPor: usuarioAtual, criadoEm: serverTimestamp(),
      });
      const dosContrato = pacotesPorContrato[c.id] || [];
      await Promise.all(dosContrato.map(p => {
        const { id: pid, contratoId, criadoEm: pce, editadoEm: pee, criadoPor: pcp, editadoPor: pep, ...prest } = p;
        return addDoc(collection(db, colPacote), {
          ...prest, contratoId: novoDoc.id, criadoPor: usuarioAtual, criadoEm: serverTimestamp(),
        });
      }));
    } catch (e) { alert('Erro ao duplicar contrato: ' + e.message); }
    setSaving(false);
  };

  const alterarStatus = async (c, status) => {
    const historico = addHistorico(c, `Status alterado para "${STATUS_LABEL[status]}"`);
    await updateDoc(doc(db, colContrato, c.id), { status, historico, editadoPor: usuarioAtual, editadoEm: serverTimestamp() });
  };

  // ── CRUD Pacote ──────────────────────────────────────────────────────────
  const openNewPacote = contratoId => {
    const dosContrato = pacotesPorContrato[contratoId] || [];
    setPacoteContratoId(contratoId); setEditPacote(null);
    setFormPacote({ ...EMPTY_PACOTE, ordem: dosContrato.length + 1, itens: emptyItens() });
    setModalPacote(true);
  };
  const openEditPacote = (contratoId, p) => {
    setPacoteContratoId(contratoId); setEditPacote(p);
    setFormPacote({
      nome: p.nome || '', ordem: p.ordem || 1,
      itens: ITEM_CATEGORIAS.map(cat => {
        const existente = (p.itens || []).find(i => i.categoria === cat);
        return { categoria: cat, incluso: existente?.incluso || '', excluso: existente?.excluso || '' };
      }),
    });
    setModalPacote(true);
  };
  const closePacote = () => { setModalPacote(false); setEditPacote(null); setPacoteContratoId(null); };

  const savePacote = async () => {
    if (!formPacote.nome.trim()) return;
    setSaving(true);
    try {
      const contrato = contratos.find(c => c.id === pacoteContratoId);
      if (editPacote) {
        await updateDoc(doc(db, colPacote, editPacote.id), {
          ...formPacote, editadoPor: usuarioAtual, editadoEm: serverTimestamp(),
        });
        if (contrato) await updateDoc(doc(db, colContrato, contrato.id), { historico: addHistorico(contrato, `Pacote "${formPacote.nome}" editado`) });
      } else {
        await addDoc(collection(db, colPacote), {
          ...formPacote, contratoId: pacoteContratoId, criadoPor: usuarioAtual, criadoEm: serverTimestamp(),
        });
        if (contrato) await updateDoc(doc(db, colContrato, contrato.id), { historico: addHistorico(contrato, `Pacote "${formPacote.nome}" adicionado`) });
      }
      closePacote();
    } catch (e) { alert('Erro ao salvar pacote: ' + e.message); }
    setSaving(false);
  };

  const duplicarPacote = async (contratoId, p) => {
    setSaving(true);
    try {
      const dosContrato = pacotesPorContrato[contratoId] || [];
      const { id, criadoEm, editadoEm, criadoPor, editadoPor, ...rest } = p;
      await addDoc(collection(db, colPacote), {
        ...rest, nome: `${p.nome} (cópia)`, ordem: dosContrato.length + 1, contratoId,
        criadoPor: usuarioAtual, criadoEm: serverTimestamp(),
      });
    } catch (e) { alert('Erro ao duplicar pacote: ' + e.message); }
    setSaving(false);
  };

  // ── Parâmetros ───────────────────────────────────────────────────────────
  const openParametros = c => { setModalParametros(c); setFormParametros({ ...emptyParametros(), ...(c.parametros || {}) }); };
  const closeParametros = () => setModalParametros(null);
  const saveParametros = async () => {
    setSaving(true);
    try {
      const historico = addHistorico(modalParametros, 'Parâmetros atualizados');
      await updateDoc(doc(db, colContrato, modalParametros.id), {
        parametros: formParametros, historico, editadoPor: usuarioAtual, editadoEm: serverTimestamp(),
      });
      closeParametros();
    } catch (e) { alert('Erro ao salvar parâmetros: ' + e.message); }
    setSaving(false);
  };

  // ── Exclusão ─────────────────────────────────────────────────────────────
  const confirmarExclusao = async () => {
    if (!delItem) return;
    setSaving(true);
    try {
      if (delItem.tipo === 'contrato') {
        const dosContrato = pacotesPorContrato[delItem.id] || [];
        await Promise.all(dosContrato.map(p => deleteDoc(doc(db, colPacote, p.id))));
        await deleteDoc(doc(db, colContrato, delItem.id));
      } else {
        const pac = pacotes.find(p => p.id === delItem.id);
        await deleteDoc(doc(db, colPacote, delItem.id));
        // o banco guarda a cópia do pacote excluído; a anotação deixa ela visível no histórico do contrato
        const contrato = contratos.find(c => c.id === pac?.contratoId);
        if (contrato) await updateDoc(doc(db, colContrato, contrato.id), { historico: addHistorico(contrato, `Pacote "${pac.nome}" excluído`) });
      }
      setDelItem(null);
    } catch (e) { alert('Erro ao excluir: ' + e.message); }
    setSaving(false);
  };

  // ── Seleção / exportação em lote ─────────────────────────────────────────
  const toggleSelect = id => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const contratosSelecionados = contratosFiltrados.filter(c => selectedIds.has(c.id));

  const toggleExpandC = id => setExpandedC(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleExpandP = id => setExpandedP(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>📋</div>
        <div style={{ color: P.primary, fontWeight: 700 }}>Carregando regras de contrato...</div>
      </div>
    </div>
  );

  return (
    <div style={{ background: P.bg, minHeight: '100vh', padding: '24px 24px 80px' }}>
      {/* Header */}
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, color: P.text }}>📋 Regras de Contrato</h1>
        <p style={{ margin: '4px 0 0', color: P.muted, fontSize: '0.83rem' }}>Coberturas contratuais (inclusos/exclusos) por unidade, contrato e pacote</p>
      </div>

      {/* Seletor de Unidade */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, background: '#f1f5f9', borderRadius: 10, padding: 3, width: 'fit-content' }}>
        {[{ k: 'Homebaby', color: '#6d28d9' }, { k: 'Vitalcare', color: '#065f46' }, { k: 'Todas', color: P.primary }].map(({ k, color }) => (
          <button key={k} onClick={() => setUnidadeSel(k)} style={{
            border: 'none', borderRadius: 8, padding: '7px 20px', cursor: 'pointer',
            fontSize: '0.85rem', fontWeight: 700, transition: 'all 0.15s',
            background: unidadeSel === k ? P.white : 'transparent',
            color: unidadeSel === k ? color : '#94a3b8',
            boxShadow: unidadeSel === k ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
          }}>{k}</button>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Buscar contrato ou pacote..."
          style={{ ...inp, maxWidth: 280, flex: 1 }} />
        <GhostBtn onClick={() => setShowFiltros(v => !v)}>🔧 Filtros avançados{hasF ? ` (${[fStatus, fDIni, fDFim, fOperadora].filter(Boolean).length})` : ''}</GhostBtn>
        <GhostBtn onClick={() => { setSelectMode(v => !v); setSelectedIds(new Set()); }}>
          {selectMode ? '✕ Cancelar seleção' : '☑ Selecionar'}
        </GhostBtn>
        {canEdit && <Btn onClick={openNewContrato}>+ Novo Contrato</Btn>}
        <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: P.muted }}>{contratosFiltrados.length} contrato{contratosFiltrados.length !== 1 ? 's' : ''}</span>
      </div>

      {showFiltros && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14, background: P.white, border: `1px solid ${P.border}`, borderRadius: 10, padding: 12 }}>
          <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={{ ...inp, maxWidth: 160 }}>
            <option value="">Todos os status</option>
            {STATUS_OPTS.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
          <input type="date" value={fDIni} onChange={e => setFDIni(e.target.value)} style={{ ...inp, maxWidth: 160 }} title="Vigência de" />
          <input type="date" value={fDFim} onChange={e => setFDFim(e.target.value)} style={{ ...inp, maxWidth: 160 }} title="Vigência até" />
          <input value={fOperadora} onChange={e => setFOperadora(e.target.value)} placeholder="Operadora (ex.: AMIL)" style={{ ...inp, maxWidth: 200 }} />
          {hasF && <GhostBtn onClick={clrF}>✕ Limpar filtros</GhostBtn>}
        </div>
      )}

      {/* Lista de contratos */}
      {Object.entries(contratosPorUnidade).map(([unidade, lista]) => (
        (unidadeSel !== 'Todas' || lista.length > 0) && (
          <div key={unidade} style={{ marginBottom: 24 }}>
            {unidadeSel === 'Todas' && (
              <h3 style={{ fontSize: '0.8rem', fontWeight: 800, color: P.muted, textTransform: 'uppercase', letterSpacing: 0.6, margin: '0 0 10px' }}>
                {unidade} ({lista.length})
              </h3>
            )}
            {lista.length === 0 && unidadeSel !== 'Todas' && (
              <div style={{ background: P.white, border: `1px dashed ${P.border}`, borderRadius: 12, padding: 40, textAlign: 'center', color: P.muted }}>
                Nenhum contrato cadastrado para {unidade}.
              </div>
            )}
            {lista.map(c => {
              const dosContrato = pacotesPorContrato[c.id] || [];
              const aberto = !!expandedC[c.id];
              return (
                <div key={c.id} style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 12, marginBottom: 12, overflow: 'hidden' }}>
                  {/* Cabeçalho do contrato */}
                  <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                    {selectMode && (
                      <input type="checkbox" checked={selectedIds.has(c.id)} onChange={() => toggleSelect(c.id)} style={{ marginTop: 4, cursor: 'pointer' }} />
                    )}
                    <div onClick={() => toggleExpandC(c.id)} style={{ cursor: 'pointer', fontSize: '0.9rem', color: P.muted, marginTop: 2 }}>{aberto ? '▼' : '▶'}</div>
                    <div style={{ flex: 1, minWidth: 200, cursor: 'pointer' }} onClick={() => toggleExpandC(c.id)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 800, fontSize: '0.95rem', color: P.text }}>📄 {c.nomeContrato}</span>
                        <StatusBadge status={c.status} small />
                        <UnidadeBadge unidade={c.unidade} small />
                      </div>
                      <div style={{ fontSize: '0.74rem', color: P.muted, marginTop: 4 }}>
                        Vigência: {fmtDate(c.dataVigencia)} · {dosContrato.length} pacote{dosContrato.length !== 1 ? 's' : ''}
                        {c.editadoPor && <> · Última modificação: {c.editadoPor} ({fmtDateTime(c.editadoEm)})</>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      <GhostBtn small title="Ver histórico" onClick={() => setHistoricoContrato(c)}>🕓</GhostBtn>
                      <GhostBtn small title="Exportar XLSX" onClick={() => exportarXLSX(c, dosContrato)}>📊</GhostBtn>
                      <GhostBtn small title="Exportar PDF" onClick={() => exportarPDF(c, dosContrato)}>🧾</GhostBtn>
                      {canEdit && <GhostBtn small title="Duplicar" onClick={() => duplicarContrato(c)}>⧉</GhostBtn>}
                      {canEdit && <GhostBtn small title="Editar" onClick={() => openEditContrato(c)}>✏️</GhostBtn>}
                      {canEdit && c.status !== 'arquivado' && (
                        <GhostBtn small title={c.status === 'ativo' ? 'Desativar' : 'Ativar'} onClick={() => alterarStatus(c, c.status === 'ativo' ? 'inativo' : 'ativo')}>
                          {c.status === 'ativo' ? '⏸' : '▶️'}
                        </GhostBtn>
                      )}
                      {canEdit && c.status !== 'arquivado' && <GhostBtn small title="Arquivar" onClick={() => alterarStatus(c, 'arquivado')}>🗄</GhostBtn>}
                      {canEdit && <GhostBtn small title="Excluir" onClick={() => setDelItem({ tipo: 'contrato', id: c.id, nome: c.nomeContrato })}>🗑</GhostBtn>}
                    </div>
                  </div>

                  {/* Corpo expandido */}
                  {aberto && (
                    <div style={{ borderTop: `1px solid ${P.border}`, padding: '14px 16px', background: '#fafbfc' }}>
                      {c.observacoes && <div style={{ fontSize: '0.8rem', color: P.text, marginBottom: 12, fontStyle: 'italic' }}>{c.observacoes}</div>}

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: P.muted, textTransform: 'uppercase' }}>📦 Pacotes ({dosContrato.length})</span>
                        {canEdit && <Btn small onClick={() => openNewPacote(c.id)}>+ Novo Pacote</Btn>}
                      </div>

                      {dosContrato.length === 0 && <div style={{ color: P.muted, fontSize: '0.82rem', padding: '10px 0' }}>Nenhum pacote cadastrado.</div>}

                      {dosContrato.map(p => {
                        const abertoP = !!expandedP[p.id];
                        const preenchidos = itensPreenchidos(p.itens);
                        return (
                          <div key={p.id} style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
                            <div style={{ padding: '9px 12px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flexWrap: 'wrap' }} onClick={() => toggleExpandP(p.id)}>
                              <span style={{ fontSize: '0.8rem', color: P.muted }}>{abertoP ? '▼' : '▶'}</span>
                              <span style={{ fontWeight: 700, fontSize: '0.85rem', color: P.text }}>📦 {p.nome}</span>
                              <span style={{ fontSize: '0.68rem', color: P.muted }}>ordem {p.ordem ?? '—'}</span>
                              {preenchidos < ITEM_CATEGORIAS.length && (
                                <Pill small label={`⚠️ ${preenchidos}/${ITEM_CATEGORIAS.length} itens`} color="#a16207" bg="#fef9c3" />
                              )}
                              <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                                {canEdit && <GhostBtn small title="Duplicar" onClick={() => duplicarPacote(c.id, p)}>⧉</GhostBtn>}
                                {canEdit && <GhostBtn small title="Editar" onClick={() => openEditPacote(c.id, p)}>✏️</GhostBtn>}
                                {canEdit && <GhostBtn small title="Excluir" onClick={() => setDelItem({ tipo: 'pacote', id: p.id, nome: p.nome })}>🗑</GhostBtn>}
                              </div>
                            </div>
                            {abertoP && (
                              <div style={{ borderTop: `1px solid ${P.border}`, overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
                                  <thead>
                                    <tr style={{ background: '#f8fafc' }}>
                                      <th style={{ padding: '6px 10px', textAlign: 'left', color: P.muted, fontWeight: 700, fontSize: '0.68rem', width: 150 }}>Categoria</th>
                                      <th style={{ padding: '6px 10px', textAlign: 'left', color: P.muted, fontWeight: 700, fontSize: '0.68rem' }}>Incluso no Pacote</th>
                                      <th style={{ padding: '6px 10px', textAlign: 'left', color: P.muted, fontWeight: 700, fontSize: '0.68rem' }}>Excluso do Pacote</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {ITEM_CATEGORIAS.map(cat => {
                                      const it = (p.itens || []).find(i => i.categoria === cat) || {};
                                      return (
                                        <tr key={cat} style={{ borderTop: '1px solid #f1f5f9' }}>
                                          <td style={{ padding: '6px 10px', fontWeight: 700, color: P.text }}>{cat}</td>
                                          <td style={{ padding: '6px 10px', color: P.text }}>{it.incluso || '—'}</td>
                                          <td style={{ padding: '6px 10px', color: P.text }}>{it.excluso || '—'}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Parâmetros */}
                      <div style={{ marginTop: 14, background: P.white, border: `1px solid ${P.border}`, borderRadius: 8, padding: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: P.muted, textTransform: 'uppercase' }}>⚙️ Parâmetros</span>
                          {canEdit && <GhostBtn small onClick={() => openParametros(c)}>✏️ Editar</GhostBtn>}
                        </div>
                        {['materiais', 'medicamentos', 'dietas'].map(k => {
                          const v = c.parametros?.[k];
                          return (
                            <div key={k} style={{ fontSize: '0.8rem', color: P.text, padding: '4px 0' }}>
                              <strong>{PARAM_LABELS[k]}:</strong> {v?.tabela || '—'}{(v?.percentual !== '' && v?.percentual != null) ? ` (${v.percentual}%)` : ''}
                              {v?.observacoes && <span style={{ color: P.muted }}> · {v.observacoes}</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      ))}

      {contratosFiltrados.length === 0 && unidadeSel === 'Todas' && (
        <div style={{ background: P.white, border: `1px dashed ${P.border}`, borderRadius: 12, padding: 50, textAlign: 'center', color: P.muted }}>
          Nenhum contrato encontrado com os filtros atuais.
        </div>
      )}

      {/* Barra de ações em lote */}
      {selectMode && selectedIds.size > 0 && (
        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: P.text, color: '#fff', borderRadius: 12, padding: '10px 18px', display: 'flex', gap: 10, alignItems: 'center', boxShadow: '0 8px 24px rgba(0,0,0,.25)', zIndex: 500 }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 700 }}>{selectedIds.size} selecionado{selectedIds.size !== 1 ? 's' : ''}</span>
          <Btn small color="#22c55e" onClick={() => exportarMultiplosXLSX(contratosSelecionados, pacotesPorContrato)}>📊 Exportar XLSX</Btn>
          <Btn small color="#3b82f6" onClick={() => exportarMultiplosPDF(contratosSelecionados, pacotesPorContrato)}>🧾 Exportar PDF</Btn>
          {selectedIds.size === 2 && <Btn small color="#8b5cf6" onClick={() => setShowCompare(true)}>⇄ Comparar</Btn>}
        </div>
      )}

      {/* ── Modal: Novo/Editar Contrato ── */}
      {modalContrato && (
        <Modal title={editContrato ? 'Editar Contrato' : 'Novo Contrato'} onClose={closeContrato}>
          <Field label="Nome do Contrato" required>
            <Inp value={formContrato.nomeContrato} onChange={v => setFormContrato(f => ({ ...f, nomeContrato: v }))} placeholder="Ex.: HB - AMIL" />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Unidade" required><Sel value={formContrato.unidade} onChange={v => setFormContrato(f => ({ ...f, unidade: v }))} options={UNIDADES} /></Field>
            <Field label="Data de Vigência"><Inp type="date" value={formContrato.dataVigencia} onChange={v => setFormContrato(f => ({ ...f, dataVigencia: v }))} /></Field>
          </div>
          <Field label="Status"><Sel value={formContrato.status} onChange={v => setFormContrato(f => ({ ...f, status: v }))} options={STATUS_OPTS} labels={STATUS_LABEL} /></Field>
          <Field label="Observações"><Txt value={formContrato.observacoes} onChange={v => setFormContrato(f => ({ ...f, observacoes: v }))} rows={3} placeholder="Notas gerais sobre o contrato" /></Field>
          {errContrato && <div style={{ color: P.red, fontSize: '0.8rem', marginBottom: 10 }}>{errContrato}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
            <Btn outline color={P.muted} onClick={closeContrato}>Cancelar</Btn>
            <Btn onClick={saveContrato} disabled={saving || !formContrato.nomeContrato.trim()}>{saving ? 'Salvando...' : 'Salvar'}</Btn>
          </div>
        </Modal>
      )}

      {/* ── Modal: Novo/Editar Pacote ── */}
      {modalPacote && (
        <Modal title={editPacote ? 'Editar Pacote' : 'Novo Pacote'} onClose={closePacote} width={780}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <Field label="Nome do Pacote" required><Inp value={formPacote.nome} onChange={v => setFormPacote(f => ({ ...f, nome: v }))} placeholder="Ex.: 24 HORAS COM VM" /></Field>
            <Field label="Ordem"><Inp type="number" min="1" value={formPacote.ordem} onChange={v => setFormPacote(f => ({ ...f, ordem: +v || 1 }))} /></Field>
          </div>
          <div style={{ marginTop: 6, marginBottom: 6, fontSize: '0.72rem', fontWeight: 700, color: P.muted, textTransform: 'uppercase' }}>Itens do pacote</div>
          <div style={{ overflowX: 'auto', border: `1px solid ${P.border}`, borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '7px 10px', textAlign: 'left', color: P.muted, fontWeight: 700, fontSize: '0.68rem', width: 150 }}>Item</th>
                  <th style={{ padding: '7px 10px', textAlign: 'left', color: P.muted, fontWeight: 700, fontSize: '0.68rem' }}>Incluso no Pacote</th>
                  <th style={{ padding: '7px 10px', textAlign: 'left', color: P.muted, fontWeight: 700, fontSize: '0.68rem' }}>Excluso do Pacote</th>
                </tr>
              </thead>
              <tbody>
                {formPacote.itens.map((it, i) => (
                  <tr key={it.categoria} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 700, color: P.text, verticalAlign: 'top' }}>{it.categoria}</td>
                    <td style={{ padding: '6px 8px', verticalAlign: 'top' }}>
                      <textarea value={it.incluso} onChange={e => setFormPacote(f => { const itens = [...f.itens]; itens[i] = { ...itens[i], incluso: e.target.value }; return { ...f, itens }; })}
                        rows={2} style={{ ...inp, resize: 'vertical', fontSize: '0.78rem' }} />
                    </td>
                    <td style={{ padding: '6px 8px', verticalAlign: 'top' }}>
                      <textarea value={it.excluso} onChange={e => setFormPacote(f => { const itens = [...f.itens]; itens[i] = { ...itens[i], excluso: e.target.value }; return { ...f, itens }; })}
                        rows={2} style={{ ...inp, resize: 'vertical', fontSize: '0.78rem' }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
            <Btn outline color={P.muted} onClick={closePacote}>Cancelar</Btn>
            <Btn onClick={savePacote} disabled={saving || !formPacote.nome.trim()}>{saving ? 'Salvando...' : 'Salvar Pacote'}</Btn>
          </div>
        </Modal>
      )}

      {/* ── Modal: Parâmetros ── */}
      {modalParametros && (
        <Modal title={`Parâmetros — ${modalParametros.nomeContrato}`} onClose={closeParametros} width={620}>
          {['materiais', 'medicamentos', 'dietas'].map(key => (
            <div key={key} style={{ border: `1px solid ${P.border}`, borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: P.text, marginBottom: 8 }}>{PARAM_LABELS[key]}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
                <Field label="Tabela de referência">
                  <Inp value={formParametros[key]?.tabela} onChange={v => setFormParametros(f => ({ ...f, [key]: { ...f[key], tabela: v } }))} placeholder="Ex.: SIMPRO" />
                </Field>
                <Field label="Percentual (%)">
                  <Inp type="number" value={formParametros[key]?.percentual} onChange={v => setFormParametros(f => ({ ...f, [key]: { ...f[key], percentual: v } }))} placeholder="Ex.: 20" />
                </Field>
              </div>
              <Field label="Observações">
                <Txt value={formParametros[key]?.observacoes} onChange={v => setFormParametros(f => ({ ...f, [key]: { ...f[key], observacoes: v } }))} rows={2} />
              </Field>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
            <Btn outline color={P.muted} onClick={closeParametros}>Cancelar</Btn>
            <Btn onClick={saveParametros} disabled={saving}>{saving ? 'Salvando...' : 'Salvar Parâmetros'}</Btn>
          </div>
        </Modal>
      )}

      {/* ── Modal: Histórico ── */}
      {historicoContrato && (
        <Modal title={`🕓 Histórico — ${historicoContrato.nomeContrato}`} onClose={() => { setHistoricoContrato(null); setVerVersao(null); }} width={620}>
          <div style={{ fontSize: '0.75rem', color: P.muted, marginBottom: 12 }}>
            💾 A cada alteração ou exclusão, o Quality guarda uma cópia de como estava antes.
          </div>
          {linhaDoTempo.length === 0 ? (
            <div style={{ color: P.muted, fontSize: '0.85rem', textAlign: 'center', padding: '20px 0' }}>Nenhum registro de alteração.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {linhaDoTempo.map((x, i) => x.h ? (
                <div key={x.k} style={{ borderLeft: `3px solid ${i === 0 ? P.primary : P.border}`, paddingLeft: 10 }}>
                  <div style={{ fontSize: '0.82rem', color: P.text, fontWeight: 600 }}>{x.h.acao}</div>
                  <div style={{ fontSize: '0.72rem', color: P.muted }}>{x.h.usuario} · {fmtDateTime(x.h.data)}</div>
                </div>
              ) : (
                <div key={x.k} style={{ borderLeft: `3px solid ${P.amber}`, paddingLeft: 10, background: '#fffbeb', borderRadius: '0 6px 6px 0', padding: '6px 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontSize: '0.8rem', color: P.text, fontWeight: 600 }}>
                        💾 Cópia guardada — {nomeDaVersao(x.v)}{x.v.operacao === 'exclusao' ? ' (excluído)' : ''}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: P.muted }}>como estava antes de {fmtDateTime(x.v.salvo_em)}</div>
                    </div>
                    <GhostBtn small onClick={() => setVerVersao(verVersao?.id === x.v.id ? null : x.v)}>{verVersao?.id === x.v.id ? 'Fechar' : 'Ver'}</GhostBtn>
                    {canEdit && <Btn small outline color={P.amber} disabled={saving} onClick={() => restaurarVersao(x.v)}>Restaurar</Btn>}
                  </div>
                  {verVersao?.id === x.v.id && (
                    <div style={{ marginTop: 8, background: P.white, border: `1px solid ${P.border}`, borderRadius: 6, padding: 10 }}>
                      <VersaoDetalhe versao={x.v} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {/* ── Modal: Comparar contratos ── */}
      {showCompare && contratosSelecionados.length === 2 && (
        <Modal title="⇄ Comparar Contratos" onClose={() => setShowCompare(false)} width={980}>
          <CompararContratos
            contratoA={contratosSelecionados[0]}
            contratoB={contratosSelecionados[1]}
            pacotesA={pacotesPorContrato[contratosSelecionados[0].id] || []}
            pacotesB={pacotesPorContrato[contratosSelecionados[1].id] || []}
          />
        </Modal>
      )}

      {/* ── Modal: Confirmar exclusão ── */}
      {delItem && (
        <Modal title="Confirmar exclusão" onClose={() => setDelItem(null)} width={420}>
          <p style={{ color: P.text, fontSize: '0.88rem', lineHeight: 1.5 }}>
            {delItem.tipo === 'contrato'
              ? <>Excluir o contrato <strong>{delItem.nome}</strong>? Todos os pacotes vinculados a ele também serão excluídos.</>
              : <>Excluir o pacote <strong>{delItem.nome}</strong>?</>}
            <br /><span style={{ color: P.red }}>Esta ação não pode ser desfeita.</span>
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
            <Btn outline color={P.muted} onClick={() => setDelItem(null)}>Cancelar</Btn>
            <Btn color={P.red} onClick={confirmarExclusao} disabled={saving}>{saving ? 'Excluindo...' : 'Excluir'}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Comparador de contratos ────────────────────────────────────────────────────
function CompararContratos({ contratoA, contratoB, pacotesA, pacotesB }) {
  const nomesPacotes = useMemo(() => {
    const set = new Set([...pacotesA.map(p => p.nome), ...pacotesB.map(p => p.nome)]);
    return [...set];
  }, [pacotesA, pacotesB]);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {[contratoA, contratoB].map(c => (
          <div key={c.id} style={{ background: '#f8fafc', borderRadius: 8, padding: 12 }}>
            <div style={{ fontWeight: 800, fontSize: '0.9rem', color: P.text }}>{c.nomeContrato}</div>
            <div style={{ fontSize: '0.76rem', color: P.muted, marginTop: 3 }}>
              {c.unidade} · {STATUS_LABEL[c.status]} · Vigência {fmtDate(c.dataVigencia)}
            </div>
          </div>
        ))}
      </div>

      {nomesPacotes.length === 0 && <div style={{ color: P.muted, textAlign: 'center', padding: 20 }}>Nenhum pacote para comparar.</div>}

      {nomesPacotes.map(nome => {
        const pA = pacotesA.find(p => p.nome === nome);
        const pB = pacotesB.find(p => p.nome === nome);
        return (
          <div key={nome} style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 700, fontSize: '0.85rem', color: P.text, marginBottom: 6 }}>📦 {nome}</div>
            <div style={{ overflowX: 'auto', border: `1px solid ${P.border}`, borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', color: P.muted, fontSize: '0.66rem', width: 130 }}>Item</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', color: P.muted, fontSize: '0.66rem' }} colSpan={2}>{contratoA.nomeContrato}</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', color: P.muted, fontSize: '0.66rem' }} colSpan={2}>{contratoB.nomeContrato}</th>
                  </tr>
                </thead>
                <tbody>
                  {ITEM_CATEGORIAS.map(cat => {
                    const itA = pA ? (pA.itens || []).find(i => i.categoria === cat) : null;
                    const itB = pB ? (pB.itens || []).find(i => i.categoria === cat) : null;
                    const diff = (itA?.incluso || '') !== (itB?.incluso || '') || (itA?.excluso || '') !== (itB?.excluso || '');
                    return (
                      <tr key={cat} style={{ borderTop: '1px solid #f1f5f9', background: diff ? '#fffbeb' : 'transparent' }}>
                        <td style={{ padding: '6px 8px', fontWeight: 700 }}>{cat}</td>
                        <td style={{ padding: '6px 8px' }}>{pA ? (itA?.incluso || '—') : <span style={{ color: P.muted }}>sem pacote</span>}</td>
                        <td style={{ padding: '6px 8px', color: P.red }}>{pA ? (itA?.excluso || '—') : ''}</td>
                        <td style={{ padding: '6px 8px' }}>{pB ? (itB?.incluso || '—') : <span style={{ color: P.muted }}>sem pacote</span>}</td>
                        <td style={{ padding: '6px 8px', color: P.red }}>{pB ? (itB?.excluso || '—') : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
